import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { isDocParserFamily, isAlternativeParser } from '../../../utils/parserTypes';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  Grid,
  Breadcrumbs,
  Link,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  MenuItem,
  InputLabel,
  Select,
  Chip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  GetApp as DownloadIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../../utils/global';
import { AuditLoggers } from '../../../utils/auditLogger';
import { useUserRights } from '../../../context/UserRightsContext';
import { hasWorkflowProductPermission } from './workflowProductScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
} from '../../../theme/harmonateStyles';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

const API_BASE = process.env.REACT_APP_API_URL;

const UnClassifiedPDFViewer = ({
  fileInstanceId,
  fileName,
  onBack,
  onBackToWorkflows,
  selectedDocumentType,
  fileDetails = {},
  productScope,
}) => {
  // Log props to debug the unitOfWorkId issue
  console.log('UnClassifiedPDFViewer props:', {
    fileInstanceId,
    fileName,
    fileDetails,
    selectedDocumentType
  });
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [pdfLoadedInViewer, setPdfLoadedInViewer] = useState(false);
  const [isEditingClassification, setIsEditingClassification] = useState(false);
  const [classificationData, setClassificationData] = useState({
    documentType: '',
    fund: '',
    fundIdTerm: '',
    parserId: '',
    workflowDefinitionSnowflakeId: '',
    multiInvestor: false,
    investor: '',
    investorIdTerm: '',
    documentTypeIncludeTerms: '',
  });
  
  const [validationErrors, setValidationErrors] = useState({
    documentType: false,
    fund: false,
    fundIdTerm: false,
    parserId: false,
    workflowDefinitionSnowflakeId: false,
    documentTypeIncludeTerms: false,
  });
  // State for dropdown options
  const [fundOptions, setFundOptions] = useState([]);
  const [investorOptions, setInvestorOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [parserOptions, setParserOptions] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  
  // State for loading and errors
  const [loading, setLoading] = useState({
    funds: false,
    investors: false,
    documentTypes: false,
    parsers: false,
    workflows: false,
  });
  
  const [error, setError] = useState({
    funds: null,
    investors: null,
    documentTypes: null,
    parsers: null,
    workflows: null,
  });
  const [displayDetails, setDisplayDetails] = useState({});
  const [isClassifying, setIsClassifying] = useState(false);
  const [isClassifying1, setIsClassifying1] = useState(false);
  const [classificationResults, setClassificationResults] = useState(null);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  
  // Redrive functionality state
  const [redriveLoading, setRedriveLoading] = useState(false);
  const [redriveConfirmOpen, setRedriveConfirmOpen] = useState(false);
  const [redriveStatusDialog, setRedriveStatusDialog] = useState({ open: false, executionArn: null, status: null });
  const [unitOfWorkId, setUnitOfWorkId] = useState(null);
  
  // Test Classifier state
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { hasPermission, isHarmonateUser } = useUserRights();
  const canEditClassification = hasWorkflowProductPermission(
    hasPermission,
    isHarmonateUser,
    productScope,
    'edit'
  );

  useEffect(() => {
    const defaultDetails = {
      documentType: fileDetails.documentType || 'Unclassified Document',
      month: fileDetails.month || '',
      quarter: fileDetails.quarter || '',
      year: fileDetails.year || new Date().getFullYear(),
      originalFileName: fileDetails.originalFileName || fileName || '',
      originalFileSize: fileDetails.originalFileSize || '',
      originalFileType: fileDetails.originalFileType || 'application/pdf',
      receivedOn: fileDetails.receivedOn || new Date().toISOString(),
      source: fileDetails.source || 'Application',
      uploadedBy: fileDetails.uploadedBy || '',
      watermarks: fileDetails.watermarks || 'Not Processed',
      transformationMap: fileDetails.transformationMap || '0',
      processTemplateId: fileDetails.processTemplateId || '',
      parserName: fileDetails.parserName || '',
      parserTemplate: fileDetails.parserTemplate || '',
    };
    setDisplayDetails(defaultDetails);
  }, [fileDetails, fileName]);

  const fetchPdfUrl = async (fileInstanceId) => {
    try {
      setPdfLoading(true);
      setPdfError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      console.log(`Fetching PDF URL for file instance ID: ${fileInstanceId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/presigned-url`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF URL: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      console.log('PDF API Response:', data);
      if (data.url) {
        setPdfUrl(data.url);
        setPdfLoadedInViewer(false); // Reset when new URL is set
        showSuccess('PDF loaded successfully');
      } else {
        throw new Error('No URL returned from API');
      }
    } catch (err) {
      console.error('Error fetching PDF URL:', err);
      setPdfError(err.message);
      showError(`Failed to load PDF: ${err.message}`, 'PDF Loading Error');
      setPdfLoadedInViewer(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleClassificationForm = async () => {
    if (!isEditingClassification && !canEditClassification) {
      showError('You do not have permission to edit workflow data', 'Access Denied');
      return;
    }
    const newEditingState = !isEditingClassification;
    setIsEditingClassification(newEditingState);
    
    // Log when user starts configuring classification
    if (!isEditingClassification) {
      try {
        const { AuditLoggers } = await import('../../../utils/auditLogger');
        await AuditLoggers.classificationConfigurationStarted(fileInstanceId, fileName);
      } catch (error) {
        console.error('Error logging classification configuration start:', error);
      }
    }
    
    if (newEditingState) {
      try {
        console.log('Loading classification data...');
        await Promise.all([
          fetchFundsAndInvestors(),
          fetchDocumentTypes(),
          fetchParsers()
        ]);
        console.log('Classification data loaded successfully');
      } catch (error) {
        console.error('Error loading classification data:', error);
        showError('Failed to load classification data. Please try again.', 'Error');
        setIsEditingClassification(false);
      }
    }
  };

  const handleClassificationChange = (field, value) => {
    setClassificationData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error when field is updated
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const handleWorkflowChange = (value) => {
    const selectedWorkflow = workflowOptions.find(w => w.id === value);
    
    setClassificationData(prev => ({
      ...prev,
      workflowDefinitionSnowflakeId: value,
      // Clear parserId if the new workflow doesn't require a parser
      ...(!isDocParserFamily(selectedWorkflow?.parserType) ? { parserId: '' } : {})
    }));
    
    if (validationErrors.workflowDefinitionSnowflakeId) {
      setValidationErrors(prev => ({
        ...prev,
        workflowDefinitionSnowflakeId: false
      }));
    }
  };

  const validateForm = () => {
    const selectedWorkflow = workflowOptions.find(
      w => w.id === classificationData.workflowDefinitionSnowflakeId
    );
    const isDocParser = isDocParserFamily(selectedWorkflow?.parserType);
    
    const errors = {
      documentType: !classificationData.documentType,
      fund: !classificationData.fund,
      fundIdTerm: !classificationData.fundIdTerm,
      // Only require parserId for DocParser workflows
      parserId: isDocParser && !classificationData.parserId,
      workflowDefinitionSnowflakeId: !classificationData.workflowDefinitionSnowflakeId,
      // documentTypeIncludeTerms is now optional, so no validation needed
    };
    
    setValidationErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSaveClassification = async () => {
    if (!canEditClassification) {
      showError('You do not have permission to edit workflow data', 'Access Denied');
      return;
    }
    if (!validateForm()) {
      showError('Please fill in all required fields', 'Validation Error');
      return;
    }
    
    // Log the classification save attempt
    try {
      const { AuditLoggers } = await import('../../../utils/auditLogger');
      await AuditLoggers.classificationConfigurationSaved(fileInstanceId, fileName, classificationData);
    } catch (error) {
      console.error('Error logging classification save:', error);
      // Continue with the save operation even if logging fails
    }
    
    try {
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('No tenant ID found. Please log in again.');
      }
      
      // Prepare payload with conditional fields
      const payload = {
        workflowDefinitionSnowflakeId: classificationData.workflowDefinitionSnowflakeId || null,
        documentTypeId: classificationData.documentType,
        fundIdTerms: classificationData.fundIdTerm ? [classificationData.fundIdTerm] : [],
        investorIdTerms: classificationData.investorIdTerm ? [classificationData.investorIdTerm] : [],
        investorId: classificationData.investor || '',
        isMultiInvestor: classificationData.multiInvestor || false,
        documentTypeIncludeTerms: classificationData.documentTypeIncludeTerms ? [classificationData.documentTypeIncludeTerms] : []
      };
      
      // Only add extractorMetadata if parserId exists
      if (classificationData.parserId) {
        payload.extractorMetadata = {
          parser_id: classificationData.parserId.trim()
        };
      }
      
      const fundId = classificationData.fund;
      if (!fundId) {
        throw new Error('Please select a fund');
      }
      const documentId = fileInstanceId;
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}conductor/funds/${fundId}/details`,
        payload,
        {
          params: {
            tenantId: tenantId,
            documentId: documentId,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (response.status === 200) {
        showSuccess('Classification saved successfully');
        setIsEditingClassification(false);
        setClassificationData({
          documentType: '',
          fund: '',
          fundIdTerm: '',
          parserId: '',
          workflowDefinitionSnowflakeId: '',
          multiInvestor: false,
          investor: '',
          investorIdTerm: '',
          documentTypeIncludeTerms: '',
        });

        // Get UOW ID and trigger redrive functionality
        await fetchUnitOfWorkAndRedrive();
      } else {
        throw new Error('Failed to save classification');
      }
    } catch (error) {
      console.error('Error saving classification:', error);
      showError(
        `Failed to save classification: ${error.response?.data?.message || error.message}`
      );
    }
  };

  const handleCancelClassification = () => {
    setClassificationData({
      documentType: '',
      fund: '',
      fundIdTerm: '',
      parserId: '',
      workflowDefinitionSnowflakeId: '',
      multiInvestor: false,
      investor: '',
      investorIdTerm: '',
      documentTypeIncludeTerms: '',
    });
    setIsEditingClassification(false);
  };

  // Redrive functionality
  const handleRedriveClick = () => {
    setRedriveConfirmOpen(true);
  };

  const handleRedriveConfirm = async () => {
    if (!unitOfWorkId) {
      showError('Unit of Work ID not found. Cannot proceed with redrive.');
      return;
    }

    try {
      setRedriveLoading(true);
      setRedriveConfirmOpen(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Starting redrive process for Unit of Work:', unitOfWorkId);

      // Step 1: Get workflow execution using unitOfWorkId
      const workflowExecutionUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/uow/${unitOfWorkId}`;
      console.log('Step 1: Fetching workflow execution from URL:', workflowExecutionUrl);
      
      const workflowExecutionResponse = await fetch(workflowExecutionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Step 1: Workflow execution response status:', workflowExecutionResponse.status);

      if (!workflowExecutionResponse.ok) {
        const errorText = await workflowExecutionResponse.text();
        console.error('Step 1: Error response text:', errorText);
        throw new Error(`Failed to get workflow execution: ${workflowExecutionResponse.status} - ${errorText}`);
      }

      const workflowData = await workflowExecutionResponse.json();
      console.log('Step 1: Workflow execution data received:', workflowData);
      const workflowExecutionId = workflowData.id;

      if (!workflowExecutionId) {
        console.error('Step 1: No workflow execution ID found in response:', workflowData);
        throw new Error('Workflow execution ID not found in response');
      }

      console.log('Step 1: Successfully found workflow execution ID:', workflowExecutionId);

      // Log the workflow redrive action
      try {
        // Check multiple possible locations for the file name
        const originalFileName = fileDetails.originalFileName || 
                               fileDetails.file_name || 
                               fileDetails.originalFileName ||
                               displayDetails.originalFileName ||
                               'unknown';
                                
        console.log('Logging workflow redrive action...', { 
          workflowExecutionId, 
          fileName: originalFileName,
          fileDetails,
          displayDetails
        });
        
        await AuditLoggers.workflowRedriveInitiated(
          workflowExecutionId,
          originalFileName,
          fileInstanceId,
          fileDetails.workflow_definition_name || 'unclassified'
        );
      } catch (logError) {
        console.error('Error logging workflow redrive:', logError);
        // Continue with redrive even if logging fails
      }

      // Step 2: Call redrive API
      const redriveUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/${workflowExecutionId}/redrive`;
      console.log('Step 2: Calling redrive API at URL:', redriveUrl);
      // QA uses dev step function name, others use their environment name
      const stepFunctionName = process.env.REACT_APP_ENV === 'qa' ? 'Conductor-v2-dev-workflow' : `Conductor-v2-${process.env.REACT_APP_ENV}-workflow`;
      const stateMachineArn = `arn:aws:states:eu-west-1:${process.env.REACT_APP_AWS_ACCOUNT_ID}:stateMachine:${stepFunctionName}`;
      console.log('Step 2: Request payload:', {
        stateMachineArn: stateMachineArn
      });

      const redriveResponse = await fetch(redriveUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stateMachineArn: stateMachineArn
        })
      });

      console.log('Step 2: Redrive response status:', redriveResponse.status);

      if (!redriveResponse.ok) {
        const errorText = await redriveResponse.text();
        console.error('Step 2: Error response text:', errorText);
        throw new Error(`Failed to redrive workflow: ${redriveResponse.status} - ${errorText}`);
      }

      const redriveData = await redriveResponse.json();
      console.log('Step 2: Redrive response data received:', redriveData);
      const newExecutionArn = redriveData.newExecutionArn;

      if (!newExecutionArn) {
        console.error('Step 2: No execution ARN found in response:', redriveData);
        throw new Error('New execution ARN not found in redrive response');
      }

      console.log('Step 2: Successfully received new execution ARN:', newExecutionArn);

      showSuccess('Workflow redrive initiated successfully');

      // Open status dialog
      setRedriveStatusDialog({
        open: true,
        executionArn: newExecutionArn,
        status: 'RUNNING'
      });

      pollRedriveStatus(newExecutionArn);

    } catch (error) {
      console.error('Error during redrive:', error);
      showError(`Failed to redrive workflow: ${error.message}`);
    } finally {
      setRedriveLoading(false);
    }
  };

  const pollRedriveStatus = async (executionArn) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const statusResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/${encodeURIComponent(executionArn)}/status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        console.log('Polling redrive status:', statusData.status);
        
        setRedriveStatusDialog(prev => ({
          ...prev,
          status: statusData.status
        }));

        if (statusData.status === 'RUNNING') {
          // Continue polling every 3 seconds
          setTimeout(() => pollRedriveStatus(executionArn), 3000);
        } else {
          // Final status reached
          if (statusData.status === 'SUCCEEDED') {
            showSuccess('Workflow redrive completed successfully');
          } else if (statusData.status === 'FAILED') {
            showError('Workflow redrive failed');
          }
        }
      } else {
        console.error('Failed to poll status:', statusResponse.status);
      }
    } catch (error) {
      console.error('Error polling redrive status:', error);
    }
  };

  const handleCloseRedriveStatus = () => {
    setRedriveStatusDialog({ open: false, executionArn: null, status: null });
  };

  // Function to fetch UOW ID and trigger redrive
  const fetchUnitOfWorkAndRedrive = async () => {
    try {
      // First check if unitOfWorkId is already available in fileDetails
      if (fileDetails.unitOfWorkId) {
        console.log('Found UOW ID in fileDetails:', fileDetails.unitOfWorkId);
        setUnitOfWorkId(fileDetails.unitOfWorkId);
        setRedriveConfirmOpen(true);
        return;
      }

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      console.log('Fetching UOW for file instance ID:', fileInstanceId);

      // Fetch unclassified file instances to get the UOW ID
      const url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/unclassified/by-tenant/${tenantId}`;
      const params = new URLSearchParams();
      
      // Add date range for broader search - use a wider range to ensure we find the file
      params.append('from', '2025-01-01T00:00:00.000Z');
      params.append('to', '2025-12-31T23:59:59.999Z');
      
      const fullUrl = `${url}?${params.toString()}`;
      console.log('Fetching unclassified files from:', fullUrl);

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch file instances: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Unclassified files response:', data);
      console.log('Looking for file with ID:', fileInstanceId);
      console.log('Available file IDs in response:', data.map(f => f.id));

      // Find the current file by its ID
      const currentFile = data.find(file => file.id === fileInstanceId);
      
      if (currentFile) {
        console.log('Found matching file:', currentFile);
        if (currentFile.unitOfWorkId) {
          console.log('Found UOW ID:', currentFile.unitOfWorkId);
          setUnitOfWorkId(currentFile.unitOfWorkId);
          
          // Show confirmation dialog for redrive
          setRedriveConfirmOpen(true);
        } else {
          console.error('File found but unitOfWorkId is missing:', currentFile);
          showError('Unit of Work ID is not available for this file. Cannot proceed with redrive.');
        }
      } else {
        console.error('File not found in API response');
        console.error('Searched for:', fileInstanceId);
        console.error('Available files:', data.map(f => ({ id: f.id, name: f.name })));
        showError('File not found in unclassified files. Cannot proceed with redrive.');
      }
    } catch (error) {
      console.error('Error fetching Unit of Work ID:', error);
      showError(`Failed to get Unit of Work information: ${error.message}`);
    }
  };

  const handleRetryPdf = () => {
    if (fileInstanceId) {
      fetchPdfUrl(fileInstanceId);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showInfo('PDF download initiated');
    }
  };

  const handleAutoClassify = async () => {
    try {
      setIsClassifying(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      // The viewer already loaded this URL. Safari reuses that cached response, which has no
      // CORS header, and fetch then fails with "Load failed". Bypass the cache.
      const pdfResponse = await fetch(pdfUrl, { cache: 'no-store', mode: 'cors' });
      if (!pdfResponse.ok) {
        throw new Error('Failed to fetch PDF content');
      }
      const pdfBlob = await pdfResponse.blob();
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName || 'document.pdf');
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('No tenant ID found in localStorage');
      }
      const response = await axios.post(
        `${API_BASE}/test-classification?tenant_id=${tenantId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            accept: 'application/json',
          },
        }
      );
      if (response.data) {
        setClassificationResults(response.data);
        setIsResultsDialogOpen(true);
        showSuccess('Document classified successfully');
      }
    } catch (error) {
      console.error('Error during auto-classification:', error);
      showError(
        `Failed to auto-classify document: ${error.response?.data?.message || error.message}`,
        'Classification Error'
      );
    } finally {
      setIsClassifying(false);
    }
  };

  const applyClassificationResult = (result) => {
    setClassificationData((prev) => ({
      ...prev,
      documentType: result.document_type_id || '',
      fund: result.fundId || '',
      fundIdTerm: result.fundIdTerm || '',
      multiInvestor: result.multiInvestor || false,
      investor: result.investorId || '',
      investorIdTerm: result.investorIdTerm || '',
      documentTypeIncludeTerms: result.documentTypeIncludeTerms?.join(', ') || '',
    }));
    setIsResultsDialogOpen(false);
    showInfo('Classification applied. Please review and save if correct.');
  };

  const handleTestClassifier = async () => {
    try {
      setIsClassifying1(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      // The viewer already loaded this URL. Safari reuses that cached response, which has no
      // CORS header, and fetch then fails with "Load failed". Bypass the cache.
      const pdfResponse = await fetch(pdfUrl, { cache: 'no-store', mode: 'cors' });
      if (!pdfResponse.ok) {
        throw new Error('Failed to fetch PDF content');
      }
      const pdfBlob = await pdfResponse.blob();
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName || 'document.pdf');
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('No tenant ID found in localStorage');
      }
      const response = await axios.post(
        `${API_BASE}/debug-classify-document?tenant_id=${tenantId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            accept: 'application/json',
          },
        }
      );
      if (response.data) {
        setTestResults(response.data);
        setIsTestDialogOpen(true);
        showSuccess('Classification test completed successfully');
      }
    } catch (error) {
      console.error('Error during test classification:', error);
      showError(
        `Failed to test classification: ${error.response?.data?.message || error.message}`,
        'Classification Error'
      );
    } finally {
      setIsClassifying1(false);
    }
  };


  const fetchFundsAndInvestors = async () => {
    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      console.error('No tenant_id found in localStorage');
      showError('Tenant ID is missing. Please log in again.', 'Authentication Error');
      return { funds: [], investors: [] };
    }
    console.log('Fetching funds and investors for tenant:', tenantId);
    setLoading((prev) => ({ ...prev, funds: true, investors: true }));
    setError((prev) => ({ ...prev, funds: null, investors: null }));
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(
        /\/+$/,
        ''
      );
      const url = `${baseUrl}/conductor/funds/by-tenant-entity?tenantId=${tenantId}`;
      console.log('Fetching funds and investors from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      console.log('Funds and Investors API response:', data);
      const funds = [];
      const investors = [];
      if (Array.isArray(data)) {
        data.forEach((item) => {
          const formattedItem = {
            id: item.id,
            name: item.fundName || `Fund ${item.id || ''}`,
            code: item.fundCode || '',
            isMultiInvestor: item.isMultiInvestor || false,
          };
          
          // If it has a fundName or fundCode, treat it as a fund
          if (item.fundName || item.fundCode) {
            funds.push(formattedItem);
          }
          // You can add additional conditions for investors if needed
        });
      }
      console.log('Formatted funds:', funds);
      console.log('Formatted investors:', investors);
      setFundOptions(funds);
      setInvestorOptions(investors);
      return { funds, investors };
    } catch (err) {
      console.error('Error fetching funds and investors:', err);
      setError((prev) => ({
        ...prev,
        funds: err.message,
        investors: err.message,
      }));
      showError(`Failed to load funds and investors: ${err.message}`, 'Error');
    } finally {
      setLoading((prev) => ({ ...prev, funds: false, investors: false }));
    }
  };

  const fetchDocumentTypes = async () => {
    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      console.error('No tenant_id found in localStorage');
      showError('Tenant ID is missing. Please log in again.', 'Authentication Error');
      return [];
    }
    console.log('Fetching document types for tenant:', tenantId);
    setLoading((prev) => ({ ...prev, documentTypes: true }));
    setError((prev) => ({ ...prev, documentTypes: null }));
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002').replace(
        /\/+$/,
        ''
      );
      const url = new URL(`${baseUrl}/conductor/document-types/by-tenant/basic`);
      url.searchParams.append('tenantId', tenantId);
      url.searchParams.append('category', 'WF');
      console.log('Fetching document types from:', url.toString());
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      console.log('Document Types API response:', data);
      const docTypesArray = Array.isArray(data) ? data : [];
      const formattedDocTypes = docTypesArray
        .filter((docType) => {
          const name = (docType.name || docType.documentTypeName || '').toLowerCase();
          return !name.includes('document types') && !name.includes('unidentified');
        })
        .map((docType) => ({
          id: docType.id || docType.documentTypeId,
          name: docType.name || docType.documentTypeName || `Document Type ${docType.id || docType.documentTypeId || ''}`,
          parserType: docType.parserType
        }));
      
      console.log('Formatted document types:', formattedDocTypes);
      setDocumentTypes(docTypesArray); // Store the full document types data
      setDocumentTypeOptions(formattedDocTypes);
      return formattedDocTypes;
    } catch (err) {
      console.error('Error fetching document types:', err);
      setError((prev) => ({ ...prev, documentTypes: err.message }));
      showError(`Failed to load document types: ${err.message}`, 'Error');
    } finally {
      setLoading((prev) => ({ ...prev, documentTypes: false }));
    }
  };

  const fetchParsers = async () => {
    try {
      setLoading(prev => ({ ...prev, parsers: true }));
      setError(prev => ({ ...prev, parsers: null }));
      
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        throw new Error('No tenant ID found. Please log in again.');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/master-parsers/tenant/${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setParserOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching parsers:', err);
      setError(prev => ({ ...prev, parsers: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, parsers: false }));
    }
  };

  const fetchWorkflows = async (documentTypeId) => {
    if (!documentTypeId) {
      setWorkflowOptions([]);
      return;
    }
    
    try {
      setLoading(prev => ({ ...prev, workflows: true }));
      setError(prev => ({ ...prev, workflows: null }));
      const tenantId = localStorage.getItem('tenant_id');
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}/document-type/${documentTypeId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Map the response to include id, name, and parserType for the dropdown
      const formattedWorkflows = Array.isArray(data) 
        ? data.map(workflow => ({
            id: workflow.workflowDefinitionSnowflakeId,
            name: workflow.workflowDefinitionName,
            parserType: workflow.parserType,
            parserKey: workflow.parserKey || '',
            rawData: workflow // Store the complete workflow data for reference
          }))
        : [];
      setWorkflowOptions(formattedWorkflows);
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError(prev => ({ ...prev, workflows: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, workflows: false }));
    }
  };

  const handleDocumentTypeChange = (value) => {
    const selectedDocType = documentTypes.find(doc => doc.id === value);
    const isDocParser = isDocParserFamily(selectedDocType?.parserType);
    
    setClassificationData(prev => ({
      ...prev,
      documentType: value,
      workflowDefinitionSnowflakeId: '', // Reset workflow when document type changes
      // Clear parserId if the new document type doesn't require a parser
      ...(prev.documentType !== value && !isDocParser ? { parserId: '' } : {})
    }));
    
    // Clear validation error when document type is selected
    if (validationErrors.documentType) {
      setValidationErrors(prev => ({
        ...prev,
        documentType: false
      }));
    }
    
    // Fetch workflows for the selected document type
    if (value) {
      fetchWorkflows(value);
    } else {
      setWorkflowOptions([]);
    }
  };

  useEffect(() => {
    if (fileInstanceId) {
      console.log('Fetching PDF...');
      fetchPdfUrl(fileInstanceId).catch((error) => {
        console.error('Error fetching PDF:', error);
        showError(`Failed to load PDF: ${error.message}`, 'Error');
      });
    }
  }, [fileInstanceId]);

  useEffect(() => {
    return () => {};
  }, []);

  const renderReadOnlyField = (value, label) => {
    const isDateField = label === 'Received On';
    return (
      <TextField
        value={isDateField && value ? formatDate(new Date(value)) : value || ''}
        disabled={true}
        fullWidth
        size="small"
        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
        label={label}
        variant="outlined"
        multiline={label === 'Transformation Map'}
        rows={label === 'Transformation Map' ? 2 : 1}
      />
    );
  };

  const detailFields = [
    { key: 'documentType', label: 'Document Type' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' },
    { key: 'originalFileName', label: 'Original File Name' },
    { key: 'originalFileSize', label: 'Original File Size' },
    { key: 'originalFileType', label: 'Original File Type' },
    { key: 'receivedOn', label: 'Received On' },
    { key: 'source', label: 'Source' },
    { key: 'uploadedBy', label: 'Uploaded By' },
    { key: 'watermarks', label: 'Watermarks' },
    { key: 'transformationMap', label: 'Transformation Map' },
    { key: 'processTemplateId', label: 'Process Template ID' },
    { key: 'parserName', label: 'Parser Name' },
    { key: 'parserTemplate', label: 'Parser Template' },
  ];

  return (
    <>
      <Box sx={{ ...harmonatePageSx, p: 0, height: '100%', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              Workflows
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBack}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <WorkIcon sx={{ fontSize: '1rem' }} />
              {selectedDocumentType || 'Documents'}
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DescriptionIcon sx={{ fontSize: '1rem' }} />
              {fileName}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, p: 2, gap: 2 }}>
          <Box sx={{ flex: 2, maxWidth: '60%' }}>
            <Paper elevation={2} sx={{ ...harmonateCardSx, bgcolor: ht.white, height: '800px', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: ht.green }} />
                  PDF Viewer
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="pdfplumber" arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isClassifying1 ? <CircularProgress size={16} /> : <SettingsIcon />}
                      onClick={handleTestClassifier}
                      disabled={!pdfUrl || pdfLoading || !pdfLoadedInViewer || isClassifying1}
                      sx={{
                        color: '#1976d2',
                        borderColor: '#1976d2',
                        '&:hover': { bgcolor: '#f0f7ff' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Test Classifier
                    </Button>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPdf}
                    disabled={!pdfUrl || pdfLoading || !pdfLoadedInViewer}
                    sx={{
                      ...harmonateUploadOutlinedSx,
                      '&:hover': { bgcolor: 'rgba(2, 107, 29, 0.08)' },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Download
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {pdfLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    <CircularProgress sx={{ color: ht.green }} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Loading PDF...
                    </Typography>
                  </Box>
                ) : pdfError ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      bgcolor: '#f5f5f5',
                      p: 3,
                    }}
                  >
                    <Alert severity="error" sx={{ mb: 2, maxWidth: '400px' }}>
                      <Typography variant="body2">Failed to load PDF: {pdfError}</Typography>
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleRetryPdf}
                      sx={{ ...harmonateUploadOutlinedSx, '&:hover': { bgcolor: 'rgba(2, 107, 29, 0.08)' } }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : pdfUrl ? (
                  <iframe
                    src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                    title={`PDF Viewer - ${fileName}`}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    onLoad={() => {
                      // Add a delay to ensure PDF is fully rendered in the iframe
                      // The onLoad event fires when the iframe loads, but PDF rendering may take longer
                      // Using a 3 second delay to ensure the PDF is completely loaded and rendered
                      setTimeout(() => {
                        setPdfLoadedInViewer(true);
                      }, 3000);
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No PDF available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
          <Box sx={{ flex: 1, maxWidth: '40%' }}>
            <Paper elevation={2} sx={{ ...harmonateCardSx, bgcolor: ht.white, height: '800px', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: ht.green }} />
                  {isEditingClassification ? 'Configure Classification' : 'Document Details'}
                </Typography>
                <Tooltip title={!canEditClassification ? 'You do not have permission to edit workflow data' : ''}>
                  <span>
                    <Button
                      variant={isEditingClassification ? 'outlined' : 'contained'}
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={toggleClassificationForm}
                      disabled={!canEditClassification && !isEditingClassification}
                      sx={{
                        ...(isEditingClassification ? harmonateCancelButtonSx : harmonateProceedButtonSx),
                        height: 'auto',
                        minHeight: 32,
                      }}
                    >
                      {isEditingClassification ? 'Cancel' : 'Configure Classification'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {isEditingClassification ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={validationErrors.documentType}>
                        <InputLabel>Document Type *</InputLabel>
                        <Select
                          value={classificationData.documentType}
                          onChange={(e) => {
                            handleDocumentTypeChange(e.target.value);
                          }}
                          label="Document Type *"
                          disabled={loading.documentTypes}
                          size="small"
                          sx={{ '& .MuiSelect-select': { height: '40px', display: 'flex', alignItems: 'center' } }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 250,
                                width: 300
                              }
                            }
                          }}
                          required
                        >
                          <MenuItem value="">
                            <em>{loading.documentTypes ? 'Loading document types...' : 'Select a Document Type'}</em>
                          </MenuItem>
                          {documentTypeOptions.map((docType) => (
                            <MenuItem key={docType.id} value={docType.id}>
                              {docType.name}
                            </MenuItem>
                          ))}
                          {!loading.documentTypes && documentTypeOptions.length === 0 && (
                            <MenuItem disabled>No document types available</MenuItem>
                          )}
                        </Select>
                        {error.documentTypes && <FormHelperText error>{error.documentTypes}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          label="Document Type Include Terms"
                          value={classificationData.documentTypeIncludeTerms || ''}
                          onChange={(e) =>
                            handleClassificationChange('documentTypeIncludeTerms', e.target.value)
                          }
                          disabled={true}
                          placeholder="Document Type Include Terms are disabled"
                        />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        options={fundOptions}
                        getOptionLabel={(option) => option.name + (option.code ? ` (${option.code})` : '')}
                        value={fundOptions.find(f => f.id === classificationData.fund) || null}
                        onChange={(event, newValue) => {
                          handleClassificationChange('fund', newValue ? newValue.id : '');
                        }}
                        disabled={loading.funds}
                        loading={loading.funds}
                        loadingText="Loading funds..."
                        noOptionsText={error.funds ? `Error: ${error.funds}` : "No funds available"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Fund *"
                            size="small"
                            error={validationErrors.fund}
                            helperText={error.funds || (validationErrors.fund ? 'This field is required' : '')}
                            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Typography variant="body2">{option.name}</Typography>
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                        label="Fund ID Term"
                        value={classificationData.fundIdTerm || ''}
                        onChange={(e) =>
                          handleClassificationChange('fundIdTerm', e.target.value)
                        }
                        disabled={!classificationData.fund}
                        error={validationErrors.fundIdTerm}
                        helperText={validationErrors.fundIdTerm ? 'This field is required' : ''}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={validationErrors.workflowDefinitionSnowflakeId}>
                        <InputLabel>Workflow *</InputLabel>
                        <Select
                          value={classificationData.workflowDefinitionSnowflakeId}
                          onChange={(e) =>
                            handleClassificationChange('workflowDefinitionSnowflakeId', e.target.value)
                          }
                          label="Workflow *"
                          disabled={loading.workflows || !classificationData.documentType}
                          size="small"
                          sx={{ '& .MuiSelect-select': { height: '40px', display: 'flex', alignItems: 'center' } }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 250,
                                width: 300
                              }
                            }
                          }}
                          required
                        >
                          <MenuItem value="">
                            <em>Select a workflow</em>
                          </MenuItem>
                          {workflowOptions.map((workflow) => (
                            <MenuItem key={workflow.id} value={workflow.id}>
                              {workflow.name || `Workflow ${workflow.id}`}
                            </MenuItem>
                          ))}
                          {!loading.workflows && workflowOptions.length === 0 && classificationData.documentType && (
                            <MenuItem disabled>No workflows available for this document type</MenuItem>
                          )}
                        </Select>
                        {error.workflows && (
                          <FormHelperText error>{error.workflows}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      {(() => {
                        const selectedWorkflow = workflowOptions.find(
                          w => w.id === classificationData.workflowDefinitionSnowflakeId
                        );
                        const showParserDropdown = isDocParserFamily(selectedWorkflow?.parserType);
                        const showParserKey = isAlternativeParser(selectedWorkflow?.parserType);
                        
                        if (showParserDropdown) {
                          return (
                          <Autocomplete
                            fullWidth
                            options={parserOptions}
                            getOptionLabel={(option) => option.parserName || `Parser ${option.parserId}`}
                            value={parserOptions.find(p => p.parserId === classificationData.parserId) || null}
                            onChange={(event, newValue) => {
                              handleClassificationChange('parserId', newValue ? newValue.parserId : '');
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Parser Name"
                                variant="outlined"
                                size="small"
                                fullWidth
                                error={validationErrors.parserId}
                                helperText={validationErrors.parserId ? 'Parser is required' : ''}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box>
                                  <Typography variant="body2">{option.parserName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {option.parserId}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            disabled={loading.parsers}
                            loading={loading.parsers}
                            loadingText="Loading parsers..."
                            noOptionsText="No parsers found"
                          />
                          );
                        }

                        if (showParserKey) {
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              label="Parser Key"
                              value={selectedWorkflow?.parserKey || ''}
                              InputProps={{ readOnly: true }}
                              helperText={
                                selectedWorkflow?.parserKey
                                  ? 'From tenant workflow (not editable here)'
                                  : 'No parser key on this workflow — set it in Tenant Workflow / Document Types'
                              }
                              error={!selectedWorkflow?.parserKey}
                            />
                          );
                        }

                        return null;
                      })()}
                    </Grid>
                    {/* Temporarily hidden - uncomment when needed */}
                    {false && (
                      <>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={classificationData.multiInvestor}
                                onChange={(e) =>
                                  handleClassificationChange(
                                    'multiInvestor',
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Multi-Investor"
                          />
                        </Grid>
                        {!classificationData.multiInvestor && (
                          <>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <FormControl fullWidth>
                                <InputLabel>Investor</InputLabel>
                                <Select
                                  value={classificationData.investor || ''}
                                  onChange={(e) =>
                                    handleClassificationChange('investor', e.target.value)
                                  }
                                  label="Investor"
                                  disabled={loading.investors}
                                  size="small"
                                  sx={{ '& .MuiSelect-select': { height: '40px', display: 'flex', alignItems: 'center' } }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 250,
                                        width: 300
                                      }
                                    }
                                  }}
                                >
                                  {/* MenuItems unchanged */}
                                </Select>
                                {error.investors && <FormHelperText error>{error.investors}</FormHelperText>}
                              </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                                label="Investor ID Term"
                                value={classificationData.investorIdTerm || ''}
                                onChange={(e) => handleClassificationChange('investorIdTerm', e.target.value)}
                                placeholder="Enter Investor ID Term"
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleSaveClassification}
                          disabled={!canEditClassification}
                          sx={harmonateProceedButtonSx}
                        >
                          Save
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    {detailFields.map((field) => (
                      <Box key={field.key}>{renderReadOnlyField(displayDetails[field.key], field.label)}</Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
        <Toast
          open={toastState.open}
          onClose={hideToast}
          message={toastState.message}
          severity={toastState.severity}
          title={toastState.title}
          autoHideDuration={toastState.autoHideDuration}
          position={toastState.position}
          variant={toastState.variant}
        />
        <Dialog
          open={isResultsDialogOpen}
          onClose={() => setIsResultsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>Document Classification Results</DialogTitle>
          <DialogContent dividers sx={harmonateDialogContentSx}>
            {classificationResults ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Document Type Matches:
                </Typography>
                {classificationResults.document_type_matches &&
                classificationResults.document_type_matches.length > 0 ? (
                  classificationResults.document_type_matches.map((match, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                          {match.document_type_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Include Terms Matched:</strong>
                        </Typography>
                        <Box sx={{ ml: 2, mb: 2 }}>
                          {match.include_terms && match.include_terms.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {match.include_terms.map((term, i) => (
                                <li
                                  key={i}
                                  style={{
                                    color: term.matched ? 'green' : 'gray',
                                    listStyleType: 'none',
                                  }}
                                >
                                  {term.term} {term.matched ? '✓' : '✗'}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No include terms defined
                            </Typography>
                          )}
                        </Box>
                        {match.exclude_terms && match.exclude_terms.length > 0 && (
                          <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>Exclude Terms Matched:</strong>
                            </Typography>
                            <Box sx={{ ml: 2 }}>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {match.exclude_terms.map((term, i) => (
                                  <li
                                    key={i}
                                    style={{
                                      color: term.matched ? 'red' : 'gray',
                                      listStyleType: 'none',
                                    }}
                                  >
                                    {term.term} {term.matched ? '✗' : '✓'}
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No document type matches found.
                  </Typography>
                )}
                {classificationResults.best_match ? (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <strong>Best Match:</strong> {classificationResults.best_match.document_type_name}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>No Best Match:</strong> No document type was identified as the best match.
                  </Alert>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No classification results available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsResultsDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Close
            </Button>
            {classificationResults?.best_match && (
              <Button
                onClick={() => applyClassificationResult(classificationResults.best_match)}
                variant="contained"
                sx={harmonateProceedButtonSx}
              >
                Apply Best Match
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Redrive Confirmation Dialog */}
        <Dialog
          open={redriveConfirmOpen}
          onClose={() => setRedriveConfirmOpen(false)}
          aria-labelledby="redrive-dialog-title"
          aria-describedby="redrive-dialog-description"
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx} id="redrive-dialog-title">
            Confirm Workflow Redrive
          </DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <Typography id="redrive-dialog-description">
              Are you sure you want to redrive the workflow for this document? This will restart the processing pipeline.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRedriveConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button 
              onClick={handleRedriveConfirm} 
              variant="contained"
              disabled={redriveLoading}
              sx={harmonateProceedButtonSx}
            >
              {redriveLoading ? 'Processing...' : 'Confirm Redrive'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Redrive Status Dialog */}
        <Dialog
          open={redriveStatusDialog.open}
          onClose={handleCloseRedriveStatus}
          aria-labelledby="redrive-status-dialog-title"
          maxWidth="sm"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx} id="redrive-status-dialog-title">
            Redrive Status
          </DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Execution ARN:</strong> {redriveStatusDialog.executionArn}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                <strong>Status:</strong>
              </Typography>
              <Chip 
                label={redriveStatusDialog.status}
                size="small"
                sx={{
                  bgcolor: redriveStatusDialog.status === 'SUCCEEDED' ? 'success.light' :
                           redriveStatusDialog.status === 'FAILED' ? 'error.light' :
                           redriveStatusDialog.status === 'RUNNING' ? 'warning.light' : 'grey.200',
                  color: redriveStatusDialog.status === 'SUCCEEDED' ? 'success.dark' :
                         redriveStatusDialog.status === 'FAILED' ? 'error.dark' :
                         redriveStatusDialog.status === 'RUNNING' ? 'warning.dark' : 'text.secondary',
                  fontWeight: 'bold'
                }}
              />
              {redriveStatusDialog.status === 'RUNNING' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </Box>
            {redriveStatusDialog.status === 'RUNNING' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The workflow is currently running. Please wait...
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseRedriveStatus}
              disabled={redriveStatusDialog.status === 'RUNNING'}
              variant="contained"
              sx={harmonateProceedButtonSx}
            >
              {redriveStatusDialog.status === 'RUNNING' ? 'Running...' : 'Close'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Test Classifier Dialog */}
        <Dialog
          open={isTestDialogOpen}
          onClose={() => setIsTestDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>Test Classification Results</DialogTitle>
          <DialogContent dividers sx={harmonateDialogContentSx}>
            {testResults ? (
              <Stack spacing={3}>
                {/* Best Document Type Match Section */}
                {testResults.best_document_type_match && (
                  <Card variant="outlined" sx={{ borderLeft: '4px solid #4caf50' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6">Best Document Type Match</Typography>
                      </Box>
                      <Box pl={3}>
                        <Typography variant="body1">
                          <strong>Document Type:</strong> {testResults.best_document_type_match.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Match Score:</strong> {testResults.best_document_type_match.score}
                        </Typography>
                        {testResults.best_document_type_match.term && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Matched Term:</strong> "{testResults.best_document_type_match.term}"
                          </Typography>
                        )}
                        {testResults.best_document_type_match.position && (
                          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Found at position: {testResults.best_document_type_match.position}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Best Fund Match Section */}
                {testResults.best_fund_match && (
                  <Card variant="outlined" sx={{ borderLeft: '4px solid #2196f3' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Best Fund Match</Typography>
                      </Box>
                      <Box pl={3}>
                        <Typography variant="body1">
                          <strong>Fund Name:</strong> {testResults.best_fund_match.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fund Code:</strong> {testResults.best_fund_match.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Match Score:</strong> {testResults.best_fund_match.score}
                        </Typography>
                        {testResults.best_fund_match.term && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Matched Term:</strong> "{testResults.best_fund_match.term}"
                          </Typography>
                        )}
                        {testResults.best_fund_match.position && (
                          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Found at position: {testResults.best_fund_match.position}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Document Type Matches */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Document Type Matches</Typography>
                    {testResults.document_type_matches?.length > 0 ? (
                      <div>
                        {testResults.document_type_matches
                          .filter(docType => (docType.include_terms?.filter(t => t.matched).length || 0) > 0)
                          .map((docType, index) => (
                          <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography sx={{ flex: 1 }}>{docType.document_type_name}</Typography>
                                <Chip 
                                  label={`${docType.include_terms.filter(t => t.matched).length} matches`} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>Matched Terms:</Typography>
                                <List dense>
                                  {docType.include_terms?.filter(t => t.matched).map((term, termIndex) => (
                                    <ListItem key={termIndex}>
                                      <ListItemIcon>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText 
                                        primary={term.term} 
                                        secondary={term.context ? `...${term.context}...` : null}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                                
                                {docType.exclude_terms?.filter(t => t.matched).length > 0 && (
                                  <Box mt={2}>
                                    <Typography variant="subtitle2" color="error" gutterBottom>Excluded Terms:</Typography>
                                    <List dense>
                                      {docType.exclude_terms.filter(t => t.matched).map((term, termIndex) => (
                                        <ListItem key={`excl-${termIndex}`}>
                                          <ListItemIcon>
                                            <CancelIcon color="error" fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText primary={term.term} />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </div>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No document type matches found.</Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Extracted Text Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">Extracted Text</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {testResults.extracted_text?.length || 0} characters
                      </Typography>
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {testResults.extracted_text || 'No extracted text available.'}
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No test results available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTestDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </>
  );
};

export default UnClassifiedPDFViewer;